import * as React from "react";
import { styled } from "@mui/material/styles";
import { PickersDay, PickersDayProps } from "@mui/x-date-pickers/PickersDay";
import { DatePicker } from "@mui/x-date-pickers";
import TextField from "@mui/material/TextField";
import { Box, IconButton, InputAdornment, InputBase, Paper, Tooltip } from "@mui/material";
import { CalendarMonth, DateRange as DateRangeIcon, EventRepeat } from "@mui/icons-material";
import moment from "moment";

const CustomPickersDay = styled(PickersDay, {
  shouldForwardProp: (prop) =>
    prop !== "dayIsBetween" &&
    prop !== "isFirstDay" &&
    prop !== "isLastDay" &&
    prop !== "_isValid" &&
    prop !== "_isUTC" &&
    prop !== "_isAMomentObject",
})(({ theme, dayIsBetween, isFirstDay, isLastDay }) => ({
  borderRadius: "50%",
  ...(dayIsBetween && {
    backgroundColor: "#eee",
    "&:focus": {
      backgroundColor: "#eee",
    },
  }),
  ...((isFirstDay || isLastDay) && {
    color: theme.palette.common.white,
    backgroundColor: theme.palette.info.main,
    "&:focus": {
      color: theme.palette.common.white,
      backgroundColor: theme.palette.info.main,
    },
  }),
}));

function Day(props) {
  const { _d, startDay, endDay, onChange, ...other } = props;

  const key = _d.toISOString();

  const day = moment(_d);

  if (!startDay && !endDay) {
    return (
      <PickersDay
        key={key}
        day={day}
        {...other}
        onDaySelect={onChange}
        outsideCurrentMonth={false}
      />
    );
  }

  const start = startDay || endDay;
  const end = endDay || startDay;

  const dayIsBetween = day.isBetween(start, end, null, "[]");
  const isFirstDay = day.isSame(start, "day");
  const isSunday = day.day() === 0;
  const isSaturday = day.day() === 6;
  const isLastDay = day.isSame(end, "day");

  return (
    <div
      style={{
        ...(dayIsBetween && {
          backgroundColor: "#eee",
        }),
        ...((isFirstDay || isSunday) && {
          borderTopLeftRadius: "50%",
          borderBottomLeftRadius: "50%",
        }),
        ...((isLastDay || isSaturday) && {
          borderTopRightRadius: "50%",
          borderBottomRightRadius: "50%",
        }),
      }}
      key={key}
    >
      <CustomPickersDay
        {...other}
        day={day}
        sx={dayIsBetween ? { px: 2.5, mx: 0 } : {}}
        dayIsBetween={dayIsBetween}
        isFirstDay={isFirstDay}
        isLastDay={isLastDay}
        onDaySelect={onChange}
        outsideCurrentMonth={false}
      />
    </div>
  );
}

function CustomTextField(props) {
  const { error, startDay, endDay } = props;

  return (
    <TextField
      {...props}
      size="small"
      error={!!error}
      helperText={error && `* ${error}`}
      sx={{ minWidth: "280px" }}
      placeholder="Start - End"
      value={`${startDay ? startDay.format("D MMM YYYY") : "Start"} - ${
        endDay ? endDay.format("D MMM YYYY") : "End"
      }`}
    />
  );
}

export default function DateRangePicker({ value, onChange, error, onGoToCurrent }) {
  const startDay = moment(value[0]).startOf("day");
  const endDay = moment(value[1]).endOf("day");
  const [input, setInput] = React.useState("");
  const datePickerRef = React.useRef(null);
  const [open, setOpen] = React.useState(false);

  const updateValue = (newVal) => {
    onChange(newVal);
  };

  const handleChange = (newValue, ...other) => {
    if (!newValue) {
      return;
    }
    updateValue(newValue);
    setOpen(false);
  };

  return (
    <DatePicker
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      value={startDay}
      label="Date Range"
      onChange={handleChange}
      showDaysOutsideCurrentMonth
      disableHighlightToday
      closeOnSelect
      autoFocus={false}
      renderDay={(params) =>
        Day({
          ...params,
          startDay,
          endDay,
          onChange: handleChange,
        })
      }
      renderInput={({ InputProps, ...params }) => (
        <Paper
          sx={{
            p: "2px 4px !important",
            display: "flex",
            alignItems: "center",
            width: "100%",
            background: "rgba(0, 0, 0, 0.04)",
          }}
        >
          <InputBase
            {...params}
            value=" "
            // eslint-disable-next-line react/jsx-no-duplicate-props
            inputProps={{
              value: `${startDay ? startDay.format("MM/DD/YYYY") : "Start"} - ${
                endDay ? endDay.format("MM/DD/YYYY") : "End"
              }`,
            }}
            sx={{ ml: 1, flex: 1, p: "2px" }}
          />
          <Box sx={{ mr: 0 }}>
            <InputAdornment>
              <Tooltip title="Go to current period">
                <IconButton onClick={onGoToCurrent}>
                  <EventRepeat fontSize="medium" />
                </IconButton>
              </Tooltip>
            </InputAdornment>
          </Box>
          <Box sx={{ mr: "6px" }}>
            <InputAdornment>
              <IconButton onClick={InputProps?.endAdornment?.props?.children?.props?.onClick}>
                <CalendarMonth fontSize="medium" />
              </IconButton>
            </InputAdornment>
          </Box>
        </Paper>
      )}
    />
  );
}
